import { useId, type ReactElement, type ReactNode } from 'react';

import { useEscapeKey } from '../hooks/useEscapeKey.js';

type ModalShellProps = {
  readonly title: string;
  readonly eyebrow?: string;
  readonly onDismiss: () => void;
  readonly children: ReactNode;
};

/**
 * The small centred dialog: one decision, taken over the page it belongs to.
 * Its room-scale counterpart is `RoomModal`, which replaces the page instead
 * of floating above it.
 */
export const ModalShell = ({
  title,
  eyebrow,
  onDismiss,
  children,
}: ModalShellProps): ReactElement => {
  const titleId = useId();
  useEscapeKey(onDismiss);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="bg-surface rounded-card border-subtle shadow-raised w-full max-w-sm border p-6">
        {eyebrow === undefined ? null : (
          <p className="text-muted mb-1.5 text-[10px] tracking-widest uppercase">{eyebrow}</p>
        )}
        <h2 id={titleId} className="font-display text-content text-2xl">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
};
